import React from 'react'
import clsx from 'clsx'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useFormContext } from 'react-hook-form'

import { Text } from '@wartek-id/text-v1'

import {
  EmptyStateAction,
  EmptyStateWithIllustration,
} from 'components/EmptyState'
import SearchResultCard from 'app/SearchPage/components/SearchResultCard'
import SearchResultCardPreloader from 'app/SearchPage/components/SearchResultCardPreloader'
import SearchResultFilterNudge from 'app/SearchPage/components/SearchResultFilterNudge'
import ScrollToTop from 'app/SearchPage/components/ScrollToTop'
import useSearchResults from 'app/SearchPage/hooks/useSearchResults'
import useSETrackEvent from 'app/SearchPage/hooks/useSETrackEvent'
import { useContentSearchFilter } from 'stores/content-search'
import { delimit } from 'utils/number'

const SearchResultTopSeparator = () => {
  const {
    formState: { errors },
  } = useFormContext()
  return (
    <div
      className={clsx(
        'sticky z-20 leading-none bg-white border-b top-[115px] border-subdued h-1',
        Boolean(errors?.searchQuery?.message) && '!top-[133px]'
      )}
    ></div>
  )
}

const SearchResultLoading = () => {
  return (
    <div className="px-4">
      {Array(5)
        .fill('')
        .map((_, index) => {
          return (
            <SearchResultCardPreloader
              key={`search-result-preloader-${index}`}
            />
          )
        })}
    </div>
  )
}

const SearchResultError = ({ onClick }) => {
  const { isFetching } = useSearchResults()
  return (
    <EmptyStateWithIllustration
      title="Terjadi kendala sistem"
      description="Silakan coba lagi dalam beberapa saat."
      fullscreen={false}
      className="w-full text-center"
    >
      <EmptyStateAction
        data-testid="error-boundary-action-primary"
        size="lg"
        className="w-full mt-2 mb-2"
        onClick={onClick}
        isLoading={isFetching}
        disabled={isFetching}
      >
        Coba lagi
      </EmptyStateAction>
    </EmptyStateWithIllustration>
  )
}

const SearchResultList = () => {
  const {
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    isSuccess,
    searchResultsData,
    searchResultsCount,
    searchResultsMeta,
    lastPageNumber,
    refetch,
  } = useSearchResults()
  const contentSearchFilterStore = useContentSearchFilter()
  const { SETrackSearchResultsPage } = useSETrackEvent()

  const clickLoadMoreHandler = React.useCallback((): void => {
    SETrackSearchResultsPage('LOAD_SEARCH_RESULTS_MORE', {
      num_results: [searchResultsCount, 'int'],
      page_num: [lastPageNumber + 1, 'int'],
    })
    refetch({ refetchPage: (page, index) => index === lastPageNumber })
  }, [SETrackSearchResultsPage, lastPageNumber, refetch, searchResultsCount])

  const clickItemHandler = React.useCallback(
    (index: number, url: string): void => {
      SETrackSearchResultsPage('CLICK_SEARCH_RESULT', {
        index: [index + 1, 'int'],
        num_results: [searchResultsCount, 'int'],
        page_num: [lastPageNumber, 'int'],
        url,
      })
    },
    [SETrackSearchResultsPage, lastPageNumber, searchResultsCount]
  )

  React.useEffect(() => {
    if (
      !contentSearchFilterStore.isShowQuickFilters &&
      !isLoading &&
      isSuccess
    ) {
      contentSearchFilterStore.setShowQuickFilters(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching, isSuccess])

  React.useEffect(() => {
    if (isLoading) {
      SETrackSearchResultsPage('LOAD_SEARCH_RESULTS_BEGIN')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading])

  React.useEffect(() => {
    if (isSuccess) {
      SETrackSearchResultsPage('LOAD_SEARCH_RESULTS_COMPLETE', {
        num_results: [searchResultsCount, 'int'],
        page_num: [1, 'int'],
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess])

  React.useEffect(() => {
    if (isFetchingNextPage) {
      SETrackSearchResultsPage('LOAD_SEARCH_RESULTS_MORE', {
        num_results: [searchResultsCount, 'int'],
        page_num: [lastPageNumber + 1, 'int'],
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetchingNextPage])

  React.useEffect(() => {
    if (searchResultsMeta?.isNextPageError) {
      SETrackSearchResultsPage('FAIL_LOAD_SEARCH_RESULTS_MORE', {
        num_results: [searchResultsCount, 'int'],
        page_num: [lastPageNumber + 1, 'int'],
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResultsMeta?.isNextPageError])

  return (
    <>
      {isLoading && (
        <>
          <SearchResultTopSeparator />
          <div className="pt-6">
            <SearchResultLoading />
          </div>
        </>
      )}

      {isSuccess && (
        <>
          <SearchResultTopSeparator />
          <div className="pt-6">
            <InfiniteScroll
              dataLength={searchResultsData.length}
              next={fetchNextPage}
              hasMore={hasNextPage}
              loader={
                searchResultsMeta?.isNextPageError ? (
                  <div className="px-12 py-8">
                    <SearchResultError onClick={clickLoadMoreHandler} />
                  </div>
                ) : (
                  <div className="px-4">
                    <div className="border-t">
                      <SearchResultCardPreloader />
                    </div>
                  </div>
                )
              }
              endMessage={
                searchResultsMeta?.isNextPageError ? (
                  <div className="px-12 py-8">
                    <SearchResultError onClick={clickLoadMoreHandler} />
                  </div>
                ) : (
                  <div className="py-6 text-center">
                    <Text variant="body" color="disabled">
                      Semua hasil sudah ditampilkan
                    </Text>
                  </div>
                )
              }
            >
              <div className="px-4 mb-4">
                <Text variant="heading-sm">
                  {delimit(searchResultsCount)} hasil
                </Text>
              </div>

              {searchResultsData.map((item, index) => (
                <>
                  {/** Positioned on the 11st order of the Search Result list item */}
                  {searchResultsCount >= 20 && index === 10 && (
                    <SearchResultFilterNudge />
                  )}

                  <SearchResultCard
                    key={`search-result-${item?.collectionId ?? index}`}
                    item={item}
                    isThumbnailRounded={item.topic !== 'Menu'}
                    onClick={() => clickItemHandler(index, item?.url ?? '')}
                  />
                </>
              ))}
              <ScrollToTop />
            </InfiniteScroll>
          </div>
        </>
      )}
    </>
  )
}

export default SearchResultList
